import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {DocumentType} from 'types/documents';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';

import usePatchDocumentConversionSettings from './services';
import {DocumentConversionOption, DocumentConversionSettings} from './types';

type DocumentConversionSettingsByTypeFormData =
    FormFields<DocumentConversionSettings>;

type FormOption = {label: string; value: DocumentConversionOption};

export type DocumentConversionSettingsBackend = Backend<{
    documentType: DocumentType;
    messages?: DocumentConversionOption;
    notes?: DocumentConversionOption;
    settingMid: string;
}>;

const DocumentConversionSettingsByType: FC = () => {
    const {data, flags} = useBackend<DocumentConversionSettingsBackend>();

    const methods = useForm<DocumentConversionSettingsByTypeFormData>({
        defaultValues: {
            conversionMessage: data.messages,
            conversionNote: data.notes,
        },
    });
    const {handleSubmit} = methods;

    const {t} = useTranslation(['Document.ConversionSettings.ByType']);

    const {mutateAsync} = usePatchDocumentConversionSettings(data.settingMid);

    const options: FormOption[] = [
        {
            label: t('options.leaveBlank'),
            value: 'leave_blank',
        },
        {
            label: t('options.copyFromPreset'),
            value: 'copy_from_preset',
        },
        {
            label: t('options.copyFromSource'),
            value: 'copy_from_source',
        },
    ];

    const onSubmit = async (
        formData: DocumentConversionSettingsByTypeFormData,
    ) => {
        mutateAsync(formData)
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (
                            response.name ===
                            'settings_document_conversion_view'
                        ) {
                            return setUrl(
                                window.Urls.settings_document_conversion_view(),
                            );
                        }
                        throw new Error('Unknown redirect');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <NavigationTabs.DocumentSettings
                    flags={flags}
                    selection="conversionSettings"
                />
                <FormHeader
                    subtitle={t('helpText', {
                        type: t(`documentTypes.${data.documentType}`),
                    })}
                    title={t(`documentTypes.${data.documentType}`)}
                />
                <SideBySide className="max-w-xl p-4">
                    {data.messages && (
                        <Radio
                            label={t('messagesLabel')}
                            name="conversionMessage"
                            options={options}
                        />
                    )}
                    {data.notes && (
                        <Radio
                            label={t('notesLabel')}
                            name="conversionNote"
                            options={options}
                        />
                    )}
                    <FormResult />
                    <FormActions align="left">
                        <SubmitButton kind="primary">
                            {t('confirm')}
                        </SubmitButton>
                    </FormActions>
                </SideBySide>
            </form>
        </FormProvider>
    );
};

export default DocumentConversionSettingsByType;
